import React, { useState, Suspense, lazy } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Layouts & Shared Components
import AdminLayout from '../../../layouts/AdminLayout';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import ErrorBoundary from '../../../shared/components/errors/ErrorBoundary';
import SearchComponent from '../../pms-agent/components/SearchComponent';

// Hooks
import useIndexCompanies from '../../../services/company/hooks/useIndexCompanies';
import { useDeleteCompany } from '../../../services/company/hooks/useDeleteCompany';

// Types
import { CompanyData } from '../../../services/company/response/IndexCompanyResponse';

// Lazy Loading Dialogs (Sesuai Praktik Terbaik di BRIEF.md)
// Ini akan memisahkan kode dialog dari bundle utama dan memuatnya hanya saat dibutuhkan.
const CreateCompanyDialog = lazy(() => import('../components/CreateCompanyDialog'));
const EditCompanyDialog = lazy(() => import('../components/EditCompanyDialog'));

const CompanyPage: React.FC = () => {
    // State untuk manajemen UI halaman
    const [page, setPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
    const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);

    // Mengambil data perusahaan menggunakan custom hook
    const { data: companiesData, isSuccess, isError, isLoading } = useIndexCompanies({
        search: searchQuery,
        page: page,
        limit: 10,
        orderBy: 'created_at',
        sort: 'desc',
        include: 'groups', // Meminta relasi 'groups' dari API
    });

    const queryClient = useQueryClient();
    const { mutate: deleteCompany, isPending: isDeleting } = useDeleteCompany();

    /**
     * Menangani aksi penghapusan perusahaan.
     * @param company - Objek perusahaan yang akan dihapus.
     */
    const onRemove = (company: CompanyData) => {
        toast.loading(`Removing ${company.name}...`, { id: 'delete-company' });

        deleteCompany({ id: company.id }, {
            onSuccess: async () => {
                toast.success('Company removed successfully', { id: 'delete-company' });
                // Me-refetch data setelah berhasil menghapus untuk memperbarui UI
                await queryClient.refetchQueries({ queryKey: ['companies'] });
            },
            onError: (error) => {
                toast.error(error.message || 'Failed to remove company', { id: 'delete-company' });
            }
        });
    };

    const handleOpenUpdate = (companyId: string) => setEditingCompanyId(companyId);
    const handleCloseUpdate = () => setEditingCompanyId(null);
    const handleOpenCreate = () => setShowCreateDialog(true);
    const handleCloseCreate = () => setShowCreateDialog(false);

    return (
        <AdminLayout>
            <ErrorBoundary fallback={
                <div className="text-center p-5">
                    <h2>An Error Occurred</h2>
                    <p>Could not display the companies page.</p>
                </div>
            }>
                <div className="container-fluid mt-5">
                    <div className="table-container">
                        <h3 className="mb-3">Companies</h3>
                        <div className="search-container mb-3">
                            <SearchComponent queryKey={searchQuery} onSearch={setSearchQuery} />
                            <button className="btn btn-primary" onClick={handleOpenCreate}>
                                Create Company
                            </button>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Logo</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <th>URL</th>
                                        <th>Groups</th>
                                        <th className='text-end'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading && <tr><td colSpan={7} className="text-center">Loading...</td></tr>}
                                    {isError && <tr><td colSpan={7} className="text-center text-danger">Failed to load data. Please try again later.</td></tr>}
                                    {isSuccess && companiesData.data.length === 0 && <tr><td colSpan={7} className="text-center">No companies found.</td></tr>}
                                    {isSuccess && companiesData.data.map((company) => (
                                        <tr key={company.id}>
                                            <td>
                                                <img
                                                    src={company.logo_url || '/bedshero_icon.png'}
                                                    alt={`${company.name} logo`}
                                                    style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', backgroundColor: '#f0f0f0' }}
                                                    onError={(e) => {
                                                        e.currentTarget.onerror = null;
                                                        e.currentTarget.src = '/bedshero_icon.png';
                                                    }}
                                                />
                                            </td>
                                            <td>{company.name}</td>
                                            <td>{company.email || 'N/A'}</td>
                                            <td>{company.phone || 'N/A'}</td>
                                            <td>
                                                <a href={company.url || '#'} target="_blank" rel="noopener noreferrer">
                                                    {company.url ? company.url.replace(/^https?:\/\//, '') : 'N/A'}
                                                </a>
                                            </td>
                                            <td>{company.groups?.map(g => g.name).join(', ') || 'N/A'}</td>
                                            <td className="text-end">
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="default" id={`dropdown-${company.id}`}>
                                                        Actions
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                                        <Dropdown.Item onClick={() => handleOpenUpdate(company.id)}>Edit</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => onRemove(company)} className='text-danger' disabled={isDeleting}>
                                                            {isDeleting ? 'Removing...' : 'Remove'}
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {isSuccess && companiesData.pagination && companiesData.data.length > 0 && (
                            <PaginationComponent
                                totalPages={companiesData.pagination.last_page}
                                currentPage={companiesData.pagination.current_page}
                                onPageChange={setPage}
                            />
                        )}
                    </div>
                </div>
            </ErrorBoundary>

            <Suspense fallback={<div className="offcanvas-backdrop fade show"></div>}>
                {showCreateDialog && (
                    <CreateCompanyDialog show={showCreateDialog} onClose={handleCloseCreate} />
                )}
                {editingCompanyId && (
                    <EditCompanyDialog
                        companyId={editingCompanyId}
                        show={!!editingCompanyId}
                        onClose={handleCloseUpdate}
                    />
                )}
            </Suspense>
        </AdminLayout>
    );
};

export default CompanyPage;